/** @jsxImportSource @emotion/react */
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { EntityDetailsWrapper, StructureOverview, StructureValidation } from 'components';
import DropInPanel from 'components/DropInPanel/DropInPanel';
import { LoadingPlaceholder } from 'components/LoadingPlaceholder/LoadingPlaceholder';
import SchemaContainer from 'components/SchemaEditor/SchemaContainer';
import EditFieldContainer from 'components/SchemaEditor/panels/EditFieldContainer';
import { StructureJSONView } from 'components/Structures/StructureJsonView';
import { StructureTesting } from 'components/Structures/StructureTesting';
import { SchemaActionTypes, SchemaContext } from 'contexts/SchemaContext';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH_ID } from 'contracts/constants';
import type { EntityTab, ISchema } from 'contracts/interfaces';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useLocation } from 'react-router-dom';
import { useGetModelQuery } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

interface StructureProps {
  isEditable: boolean;
}
const Structure: FC<StructureProps> = ({ isEditable }) => {
  const { pathname } = useLocation();
  const ctx = useContext(SchemaContext);
  const [renderEditor, setRenderEditor] = useState<string | undefined>(undefined);
  // const [selectedMockData, setSelectedMockData] = useState<string | undefined>(undefined);
  const params = getParams(pathname, [ENTITY_PATH_ID]) as {
    entity: string;
    entityId: string;
    packageId: string;
    packages: string;
    versionUuid: string;
  };

  const {
    loading: loadingModel,
    data: modelData,
    refetch
  } = useGetModelQuery({
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    ...APOLLO_BASE_OPTIONS
  });

  useEffect(() => {
    if (modelData?.getModel) {
      ctx?.dispatch({
        type: SchemaActionTypes.INIT_SCHEMA,
        payload: {
          schema: modelData.getModel.schema as ISchema,
          uuid: modelData.getModel.uuid,
          name: modelData.getModel.name,
          locked: !isEditable
        }
      });
    }
  }, [modelData]);

  const onStructureChange = (structure: ISchema): void => {
    if (ctx?.state.schema._meta && structure) {
      const meta = ctx?.state.schema._meta as IPropertyBag;
      if (meta.name) {
        structure._meta = { ...(structure._meta as IPropertyBag), name: meta.name };
      }
      ctx.dispatch({
        type: SchemaActionTypes.INIT_SCHEMA,
        payload: {
          schema: structure,
          uuid: ctx.state.uuid!,
          name: ctx.state.name,
          locked: !isEditable
        }
      });
    }
  };

  // const [getMockData, { loading, data: mockData }] = useGetMockDataLazyQuery(CACHE_FIRST);

  const loadingDiv = loadingModel && ctx?.state.uuid ? <LoadingPlaceholder /> : null;
  const tabs: EntityTab[] = [
    {
      label: 'Overview',
      isActive: true,
      content: loadingDiv || (
        <StructureOverview
          key={`Overview:${params.versionUuid}:${params.entityId}`}
          params={params}
          isEditable={isEditable}
        />
      )
    },
    {
      label: 'Schema',
      content: loadingDiv || (
        <SchemaContainer
          key={`Schema:${params.versionUuid}:${params.entityId}`}
          params={params}
          locked={isEditable === false}
        />
      )
    },
    {
      label: 'Validation',
      content: loadingDiv || (
        <StructureValidation
          key={`Validation:${params.versionUuid}:${params.entityId}`}
          params={params}
          locked={isEditable === false}
        />
      )
    },
    {
      label: 'JSON',
      content: loadingDiv || (
        <StructureJSONView
          key={`JSON:${params.versionUuid}:${params.entityId}`}
          model={{
            uuid: ctx?.state.uuid || '',
            packageVersionUuid: params.versionUuid,
            schema: ctx?.state?.schema || modelData?.getModel.schema,
            name: ctx?.state.name || modelData?.getModel.name || '',
            description: modelData?.getModel.description || ''
          }}
        />
      )
    },
    {
      label: 'Testing',
      content: (
        <StructureTesting
          structure={ctx?.state.schema}
          structureUuid={ctx?.state.uuid}
          packageVersionUuid={params.versionUuid}
          onStructureChange={onStructureChange}
        />
      )
    }
  ];

  const onChange = (tab: any): boolean | undefined | void => {
    // Hide editor overlay when tab not schema
    setRenderEditor(tab === 'Schema' /* || tab === 'Testing' */ ? tab : undefined);
  };

  return (
    <DndProvider key={`${params.versionUuid}:${params.entityId}`} backend={isMobile ? TouchBackend : HTML5Backend}>
      <EntityDetailsWrapper tabs={tabs} maxWidth={900} onChange={onChange} />
      <DropInPanel
        isHidden={renderEditor === undefined}
        displayFn={() =>
          /* renderEditor === 'Testing' || */ ctx?.state.selectedField !== undefined &&
          ctx?.state.selectedField !== null
        }
      >
        <EditFieldContainer key={ctx?.state.selectedField?.join('/') || ''} />
      </DropInPanel>
    </DndProvider>
  );
};

export default Structure;
